/**
 * LauncherHero (spec 015 · T060).
 *
 * Editorial home-page hero per `tmp/platform-mockups/launcher.html`:
 *   - Kicker: "friday · april 17 · 2026" (uppercase, mono, with leading rule)
 *   - H1: Fraunces italic "Good morning, Yash." + Fraunces roman drop line
 *         carrying the atmospheric subtitle
 *   - A single shared card on the right holding: live h:mm AM/PM clock
 *     (re-renders each minute) + date line + WeatherCard (divider, temp/
 *     condition, location chip) — one surface, per mockup `.home-card`.
 *
 * Typography and color live entirely in CSS custom properties declared on
 * the Nerdy Paradise theme (tokens.css). No hardcoded colors here.
 */
import { useEffect, useMemo, useState } from 'react';

import { displayNameFromEmail } from '@/utils/displayNameFromEmail';
import { getGreeting, type Greeting } from '@/utils/getGreeting';
import type { LauncherWeather } from '@/services/launcher';
import WeatherCard from './WeatherCard';

interface LauncherHeroProps {
  email: string;
  /** Preferred first name from Google OAuth (spec 015). Overrides email derivation. */
  givenName?: string | null;
  /** Full display name from Google OAuth. Used as a fallback when givenName is missing. */
  displayName?: string | null;
  /** Weather to compose into the shared clock/date/weather card. Null renders WeatherCard's own empty state. */
  weather?: LauncherWeather | null;
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatClock(d: Date): { time: string; meridiem: string } {
  const h24 = d.getHours();
  const h12 = ((h24 + 11) % 12) + 1;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { time: `${h12}:${mm}`, meridiem: h24 >= 12 ? 'pm' : 'am' };
}

function formatDateKicker(d: Date): string {
  // "friday · april 17 · 2026"
  return `${WEEKDAYS[d.getDay()]} · ${MONTHS[d.getMonth()]} ${d.getDate()} · ${d.getFullYear()}`;
}

function formatDateLine(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Stable period key so the random subtitle pick from getGreeting() doesn't
 * re-roll every minute — only when the window boundary crosses.
 */
function periodKey(d: Date): string {
  const h = d.getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
}

export default function LauncherHero({
  email,
  givenName,
  displayName: displayNameProp,
  weather = null,
}: LauncherHeroProps) {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const currentPeriod = periodKey(now);
  const greeting = useMemo<Greeting>(
    () => getGreeting(now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPeriod],
  );

  const displayName = useMemo(() => {
    // Preference order (spec 015):
    //   1. Google OAuth `given_name` — "Yashraj"
    //   2. First token of Google `name` — e.g. "Yashraj Gandhi" → "Yashraj"
    //   3. Fall back to email-derived name (or empty for run-on emails)
    if (givenName?.trim()) return givenName.trim();
    if (displayNameProp?.trim()) {
      return displayNameProp.trim().split(/\s+/)[0] ?? '';
    }
    const full = displayNameFromEmail(email);
    return full.split(' ')[0] ?? '';
  }, [email, givenName, displayNameProp]);

  const { time, meridiem } = formatClock(now);
  const dateKicker = formatDateKicker(now);
  const dateLine = formatDateLine(now);

  return (
    <section className="launcher-hero" data-testid="launcher-hero">
      <div className="launcher-hero__left">
        <div
          className="launcher-hero__kicker"
          data-testid="launcher-hero-datekicker"
        >
          {dateKicker.toLowerCase()}
        </div>

        <h1 className="launcher-hero__headline">
          <span className="launcher-hero__greeting-line">
            {displayName ? `${greeting.phrase}, ${displayName}` : greeting.phrase}
            <em className="launcher-hero__period">.</em>
          </span>
          <span
            className="launcher-hero__drop"
            data-testid="launcher-hero-subtitle"
          >
            {greeting.subtitle}
          </span>
        </h1>
      </div>

      <aside className="launcher-hero__card" aria-label="Current time and weather">
        <div className="launcher-hero__card-row">
          <div
            className="launcher-hero__clock"
            data-testid="launcher-hero-clock"
            aria-live="polite"
          >
            {time}
            <span className="launcher-hero__clock-meridiem"> {meridiem}</span>
          </div>
        </div>
        <div className="launcher-hero__date-line">{dateLine}</div>
        <WeatherCard weather={weather} />
      </aside>
    </section>
  );
}
